import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchBlogPosts } from '../services/api';
import { BlogPost } from '../types/blog';
import dataEmptyImg from '../assets/images/data_empty.png';

const Blog: React.FC = () => {
    const { t, i18n } = useTranslation();
    const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    // Map i18n language code to API language code
    const getLanguageCode = (lang: string): string => {
        // i18next might return language codes like 'en-US', we only need 'en'
        const langCode = lang.split('-')[0].toLowerCase();
        // Map supported languages
        const supportedLanguages = ['en', 'zh', 'fr', 'es'];
        return supportedLanguages.includes(langCode) ? langCode : 'en';
    };

    useEffect(() => {
        const loadBlogPosts = async () => {
            try {
                setLoading(true);

                const currentLang = getLanguageCode(i18n.language);

                // Fetch blog posts with language fallback to English
                const response = await fetchBlogPosts({
                    lang: currentLang,
                    fallbackLang: 'en',
                    pageSize: 3, // Show only 3 posts on home page
                    featuredOnly: false,
                });

                setBlogPosts(response.posts);
            } catch (err) {
                console.error('Failed to load blog posts:', err);
                setBlogPosts([]);
            } finally {
                setLoading(false);
            }
        };

        loadBlogPosts();
    }, [i18n.language, t]); // Re-fetch when language changes

    // Format date for display
    const formatDate = (dateString: string): string => {
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString(i18n.language, {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    return (
        <div className="section-blog">
            <div className="padding-global">
                <div className="container-large">
                    <div className="padding-section-large padding-bottom-top-medium">
                        <div className="blog-component">
                            <div className="blog-top-content-wrapper">
                                <div className="heading-wrapper">
                                    <h2 className="heading-style-h2">{t('blog.heading')}</h2>
                                    <div className="line-color-pink" style={{ width: '100%', height: '15px' }}></div>
                                </div>
                                <a href="/blog" className="button w-button">{t('blog.viewMore')}</a>
                            </div>

                            <div className="padding-bottom padding-large"></div>

                            {loading ? (
                                <div style={{ textAlign: 'center', padding: '3rem' }}>
                                    <p>{t('blog.loading', 'Loading...')}</p>
                                </div>
                            ) : blogPosts.length === 0 ? (
                                /* Empty State - consistent with BlogPage */
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '4rem 2rem',
                                }}>
                                    <img
                                        src={dataEmptyImg}
                                        alt={t('blog.empty', 'Ideas in progress')}
                                        style={{ width: '200px', marginBottom: '1.5rem' }}
                                    />
                                    <p style={{
                                        color: '#999',
                                    }}>
                                        {t('blog.empty', 'Ideas in progress')}
                                    </p>
                                </div>
                            ) : (
                                <div className="blog-collection-list-wrapper">
                                    <div role="list" className="blog-collection-list">
                                        {blogPosts.map((post) => (
                                            <div key={post.id} role="listitem" className="blog-collection-item">
                                                <div className="blog-item">
                                                    <img
                                                        src={post.image}
                                                        loading="lazy"
                                                        alt={post.title}
                                                        className="blog-image"
                                                    />
                                                    <div className="blog-details-content">
                                                        <div className="blog-top-content">
                                                            <div className="text-size-medium text-color-green">
                                                                {formatDate(post.date)}
                                                            </div>
                                                            <img
                                                                src="https://cdn.prod.website-files.com/65d1995bebc2cbfa914e45d5/65d6d4d037b79065abed9fcc_Frame%2045.svg"
                                                                loading="lazy"
                                                                alt=""
                                                            />
                                                            <div className="text-size-medium text-color-green">
                                                                {post.category_name}
                                                            </div>
                                                        </div>
                                                        <div className="padding-bottom padding-xxsmall"></div>
                                                        <div className="blog-content">
                                                            <h5 className="heading-style-h5">{post.title}</h5>
                                                            <a href={`/blog#${post.slug}`} className="blog-bottom">
                                                                <div className="text-block">{t('blog.readMore')}</div>
                                                                <img
                                                                    src="https://cdn.prod.website-files.com/65d1995bebc2cbfa914e45d5/65d6d655572382ae0996564c_arrow-right.svg"
                                                                    loading="lazy"
                                                                    alt=""
                                                                    className="blog-arrow"
                                                                />
                                                            </a>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Blog;
